import React, { useState, useEffect, useContext } from 'react';
import { Button, Table, Typography, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import html2pdf from "html2pdf.js";
import axios from 'axios';
import { AuthContext } from "../context/AuthContext";

const { Title, Text } = Typography;

export default function ViewNocForm({ employee, backurl, bearerToken, requestId }) {
    const [formData, setFormData] = useState({
        name: employee?.first_name || '',
        empNo: employee?.emp_code || '',
        designation: employee?.designation_name || '',
        dept: employee?.department_name || '',
        joiningDate: employee?.date_of_joining || '',
        resignationDate: employee?.date_of_leaving || '',
        relievingDate: employee?.last_working_day || '',
        address: '',
        phone: ''
    });

    const { authData, backUrl } = useContext(AuthContext);

    // Saved clearance data from backend
    const [clearanceFormData, setClearanceFormData] = useState(null);
    const [deptRows, setDeptRows] = useState([]);
    const [itRows, setItRows] = useState([]);
    const [financeRows, setFinanceRows] = useState([]);
    const [hrRows, setHrRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const currentDate = new Date().toLocaleDateString("en-GB");

    useEffect(() => {
        fetchClearanceForm();
    }, []);

    const fetchClearanceForm = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${backUrl}/api/clearance/requests/?employee=${employee.id}`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });

            if (response.data.results?.length > 0) {
                const form = response.data.results[0];
                setClearanceFormData(form);

                // Fetch each section's saved data
                await Promise.all([
                    fetchDeptSection(form.id),
                    fetchItSection(form.id),
                    fetchFinanceSection(form.id),
                    fetchHrSection(form.id)
                ]);
            }
        } catch (error) {
            console.error('Error fetching clearance form:', error);
            message.error('Could not load clearance data');
        } finally {
            setLoading(false);
        }
    };

    const fetchDeptSection = async (formId) => {
        try {
            const res = await axios.get(`${backUrl}/api/clearance/dept-section/${requestId || formId}/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            const items = res.data.items?.map((item, i) => ({
                key: item.id,
                item_name: item.item_name,
                status: item.status
            })) || [];
            console.log('Dept section loaded:', items.length, 'items');
            setDeptRows(items);
        } catch (e) {
            console.error('Dept fetch failed', e);
            setDeptRows([]);
        }
    };

    const fetchItSection = async (formId) => {
        try {
            const res = await axios.get(`${backUrl}/api/clearance/it-section/${requestId || formId}/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            const items = res.data.items?.map((item, i) => ({
                key: item.id,
                item_name: item.item_name,
                status: item.response_value || item.status
            })) || [];
            console.log('IT section loaded:', items.length, 'items');
            setItRows(items);
        } catch (e) {
            console.error('IT fetch failed', e);
            setItRows([]);
        }
    };

    const fetchFinanceSection = async (formId) => {
        try {
            const res = await axios.get(`${backUrl}/api/clearance/finance-section/${requestId || formId}/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            const items = res.data.items?.map((item, i) => ({
                key: item.id,
                item_name: item.item_name,
                amount: item.amount
            })) || [];
            console.log('Finance section loaded:', items.length, 'items');
            setFinanceRows(items);
        } catch (e) {
            console.error('Finance fetch failed', e);
            setFinanceRows([]);
        }
    };

    const fetchHrSection = async (formId) => {
        try {
            const res = await axios.get(`${backUrl}/api/clearance/hr-section/${requestId || formId}/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            const items = res.data.items?.map((item, i) => ({
                key: item.id,
                item_name: item.item_name,
                status: item.status
            })) || [];
            console.log('HR section loaded:', items.length, 'items');
            setHrRows(items);
            // Also set address & phone from HR section
            setFormData(prev => ({
                ...prev,
                address: res.data.correspondence_address || '',
                phone: res.data.phone_number || ''
            }));
        } catch (e) {
            console.error('HR fetch failed', e);
            setHrRows([]);
        }
    };

    const handleDownloadPDF = () => {
        const element = document.getElementById("noc-pdf");
        const options = {
            margin: [10, 10, 10, 10],
            filename: `Employee_Clearance_${employee?.emp_code || "Form"}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["css", "legacy"] }
        };
        html2pdf().set(options).from(element).save();
    };

    // View-only table columns (no inputs, no actions)
    const viewColumns = (type) => [
        {
            title: 'Sr. No.',
            dataIndex: 'srNo',
            key: 'srNo',
            width: 80,
            align: 'center',
            render: (_, __, index) => index + 1
        },
        {
            title: type === 'finance' ? 'Item Name' : 'Particulars / List of items',
            dataIndex: 'item_name',
            key: 'item_name',
            render: (text) => <Text>{text || '-'}</Text>
        },
        {
            title: type === 'finance'
                ? 'Amt. to be deducted in F&F settlement'
                : 'Status (Pending/Completed or Returned/Not returned)',
            dataIndex: type === 'finance' ? 'amount' : 'status',
            key: type === 'finance' ? 'amount' : 'status',
            width: 250,
            render: (text) => <Text>{text || '-'}</Text>
        }
    ];

    // Only department 25 (HR) can view all sections
    // Other departments can only view their own section
    const isHR = authData?.employee?.department === 25;

    const isDeptVisible = isHR || authData?.employee?.department_name === employee?.department_name;

    const isItVisible = isHR || authData?.employee?.department === 12

    const isFinanceVisible = isHR || authData?.employee?.department === 14

    const isHrVisible = isHR ? hrRows.length > 0 : false;

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px', background: '#fff' }}>
            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text>Loading clearance data...</Text>
                </div>
            ) : (
                <>
                    <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-container { padding: 40px !important; }
          .page-break { page-break-before: always; }
          input, textarea { border: none !important; }
          .ant-table { font-size: 12px !important; }
          .section-header { background: #f0f0f0 !important; }
        }
        .page-break { page-break-before: always; }
        .section-header { background: #f0f0f0; padding: 8px 12px; font-weight: bold; font-size: 16px; margin: 20px 0 10px 0; }
        .signature-line { border-bottom: 1px solid #000; display: inline-block; min-width: 200px; margin: 0 10px; }
      `}</style>
                    <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={handleDownloadPDF}
                        className="no-print"
                        style={{ color: 'red', background: 'white', marginBottom: 16 }}
                    >
                        Download PDF
                    </Button>

                    <div className="print-container" id="noc-pdf">


                        {/* Page 1 - Employee Details */}
                        <div>
                            <div style={{ textAlign: 'center', marginBottom: 30 }}>
                                <Title level={3} style={{ margin: 0 }}>Baashyaam Constructions Pvt Ltd</Title>
                                <Title level={4} style={{ margin: '10px 0 0 0' }}>Employee Clearance Form</Title>
                            </div>

                            <div style={{ marginBottom: 30, width: '700px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Name:</Text>
                                    <Text>{formData.name}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Employee Code:</Text>
                                    <Text>{formData.empNo}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Organisation:</Text>
                                    <Text>{employee.organisation_name}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Designation:</Text>
                                    <Text>{formData.designation}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Department:</Text>
                                    <Text>{formData.dept}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Joining Date:</Text>
                                    <Text>{formData.joiningDate}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Text strong>Resignation Date:</Text>
                                    <Text>{formData.resignationDate}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text strong>Relieving Date:</Text>
                                    <Text>{formData.relievingDate}</Text>
                                </div>
                            </div>

                            {/* Department Section - View Only */}
                            {isDeptVisible && (
                                <div>
                                    <div className="section-header">CLEARANCE FROM DEPARTMENT HEAD</div>
                                    <Table
                                        columns={viewColumns('dept')}
                                        dataSource={deptRows}
                                        pagination={false}
                                        bordered
                                        size="middle"
                                    />
                                    <div style={{ marginTop: 20, lineHeight: '2.5' }}>
                                        <div>Employee Signature: <span className="signature-line"></span> Date: {currentDate}</div>
                                        <div>Signature of person to whom handing over was done: <span className="signature-line"></span> Date: {currentDate}</div>
                                        <div>Signature of Departmental Head: <span className="signature-line"></span> Date: {currentDate}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* IT Section */}
                        {isItVisible && (
                            <div className="page-break">
                                <div className="section-header">CLEARANCE FROM - IT</div>
                                <Table
                                    columns={viewColumns('it')}
                                    dataSource={itRows}
                                    pagination={false}
                                    bordered
                                    size="middle"
                                />
                                <div style={{ marginTop: 20 }}>
                                    <Text strong>Signature (IT):</Text>
                                    <span className="signature-line"></span>
                                    <Text strong>Date:</Text>
                                    <span style={{ marginLeft: 8 }}>{currentDate}</span>
                                </div>
                            </div>
                        )}

                        {/* Finance Section */}
                        {isFinanceVisible && (
                            <div className={authData?.employee?.department === 14 ? "page-break" : ""}>
                                {authData?.employee?.department === 14 && (
                                    <div style={{ textAlign: 'center', marginBottom: 30 }}>
                                        <Title level={3}>Baashyaam Constructions Pvt Ltd</Title>
                                        <Title level={4}>Employee Clearance Form</Title>
                                    </div>
                                )}
                                <div className="section-header">CLEARANCE FROM FINANCE</div>
                                <Table
                                    columns={viewColumns('finance')}
                                    dataSource={financeRows}
                                    pagination={false}
                                    bordered
                                    size="middle"
                                />
                                <div style={{ marginTop: 20 }}>
                                    <Text strong>Signatures: (Finance)</Text>
                                    <span className="signature-line"></span>
                                    <Text strong>Date:</Text>
                                    <span style={{ marginLeft: 8 }}>{currentDate}</span>
                                </div>
                            </div>
                        )}

                        {/* HR Section - Last Page */}
                        {isHrVisible && (
                            <div className="page-break">
                                <div className="section-header">CLEARANCE FROM HR</div>
                                <Table
                                    columns={viewColumns('hr')}
                                    dataSource={hrRows}
                                    pagination={false}
                                    bordered
                                    size="middle"
                                />

                                <div style={{ marginTop: 30, marginBottom: 20 }}>
                                    <Text strong>
                                        CLEARANCE FROM HR (to be given only after obtaining all the above clearances and completion of exit interview)
                                    </Text>
                                </div>

                                <div style={{ marginBottom: 30 }}>
                                    <Text strong>Signature: (HR)</Text>
                                    <span className="signature-line"></span>
                                    <Text strong>Date:</Text>
                                    <span style={{ marginLeft: 8 }}>{currentDate}</span>
                                </div>

                                <div style={{ border: '1px solid #d9d9d9', padding: 20 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 15 }}>
                                        Address & Phone No. for further Correspondence:
                                    </Text>
                                    <Text>{formData.address || 'Not provided'}</Text>
                                    <br /><br />
                                    <Text strong>Phone No.:</Text> <Text>{formData.phone || 'Not provided'}</Text>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}